import { intervalDays, nextOccurrences, rechainTaper } from '@/common/medicationSchedule';
import { filledTimes, isWindowComplete, unitName, usesDayOfWeek, windowRepeat } from '@/common/medicationWindow';

/**
 * The taper step's options and rules, in one place for both hosts: the patient taper dialog
 * (BnMedicationTaperDialog) and the preset taper editor. One source means one set of options —
 * a preset offers exactly what the patient taper offers, minus what is supplied when it is applied.
 */

// A step is an administration window like any other, so it offers every type one can be —
// including a hold, which is how a taper pauses without the chain being broken.
export const TAPER_STEP_TYPE_OPTIONS = [
    { value: 'scheduled', title: 'Scheduled' },
    { value: 'prn', title: 'PRN (as needed)' },
    { value: 'hold', title: 'Hold' },
];

export const TAPER_DURATION_UNIT_OPTIONS = [
    { value: 'd', title: 'Day(s)' },
    { value: 'wk', title: 'Week(s)' },
    { value: 'mo', title: 'Month(s)' },
];

// Offered, not enforced: a combobox, so a longer step is still typeable.
export const TAPER_STEP_DURATION_OPTIONS = Array.from({ length: 30 }, (item, index) => String(index + 1));

export const TAPER_AFTER_FINAL_OPTIONS = [
    { value: 'stop', title: 'Stop medication' },
    { value: 'continue', title: 'Continue final dose until cancelled' },
];

/**
 * A step's 'Continue for' as the stepDuration Quantity a segment stores.
 *
 * @param {{durationValue?: *, durationUnit?: string}} step
 * @param {{unbounded?: boolean}} [options] - the final step running until cancelled has no duration
 * @returns {object|undefined}
 */
export const taperStepDuration = (step, { unbounded = false } = {}) => {
    if (unbounded) return undefined;
    const value = Number(step?.durationValue);
    if (!Number.isFinite(value) || value <= 0) return undefined;
    return { value, unit: unitName(step.durationUnit, value), system: 'http://unitsofmeasure.org', code: step.durationUnit };
};

/**
 * Whether the step runs until cancelled: the final step, when the taper continues after it.
 *
 * @param {number} index
 * @param {number} stepCount
 * @param {'stop'|'continue'} [afterFinal]
 * @returns {boolean}
 */
export const isUnboundedTaperStep = (index, stepCount, afterFinal) => index === stepCount - 1 && afterFinal === 'continue';

/**
 * The step windows chained from the taper start: one dosage shell per step, its boundsPeriod set
 * by rechainTaper. The resolver owns every start, so a gap or an overlap is unrepresentable.
 *
 * @param {Array<object>} steps - step windows
 * @param {string} start - yyyy-MM-dd
 * @param {{afterFinal?: 'stop'|'continue'}} [options]
 * @returns {Array<object>} [] with no start, or when the steps cannot be chained
 */
export const chainTaperSteps = (steps, start, { afterFinal } = {}) => {
    if (!start) return [];
    const list = steps || [];
    try {
        const shells = list.map((step, index) => ({
            stepDuration: taperStepDuration(step, { unbounded: isUnboundedTaperStep(index, list.length, afterFinal) }),
            timing: { repeat: windowRepeat(step) },
        }));
        return rechainTaper(shells, start);
    } catch {
        return [];
    }
};

/**
 * The stretch a chained step covers, both ends stated: a PRN step states no times or days, so its
 * stretch is the only thing that says when it runs.
 *
 * @param {{start?: string, end?: string}} [bounds] - a boundsPeriod
 * @param {(date: string) => string} formatDay - how a day reads to this user
 * @returns {string} '' with no start
 */
export const taperStepRange = (bounds, formatDay) => {
    if (!bounds?.start) return '';
    return bounds.end ? `${formatDay(bounds.start)} to ${formatDay(bounds.end)}` : `${formatDay(bounds.start)} onward`;
};

/**
 * Indexes of the Scheduled steps, with their times and days stated, whose stretch of the chain
 * holds no dose: a 'Continue for' shorter than the step's own interval — 3 days on an every-week
 * step — occupies the chain and administers nothing.
 *
 * @param {Array<object>} steps - step windows
 * @param {Array<object>} shells - chainTaperSteps(steps, ...)
 * @returns {number[]}
 */
export const emptyTaperSteps = (steps, shells) =>
    (shells || [])
        .map((shell, index) => index)
        .filter((index) => {
            const step = steps?.[index];
            if (!step || step.type !== 'scheduled') return false;
            if (!filledTimes(step).length) return false;
            if (usesDayOfWeek(step) && !(step.dayOfWeek || []).length) return false;
            return nextOccurrences(shells[index], { limit: 1 }).length === 0;
        });

/**
 * A step's own dose as a Quantity, in the unit of the dose it descends from.
 *
 * @param {*} doseValue
 * @param {object} [unitSource] - a Quantity whose unit, system and code the step shares
 * @returns {object|undefined}
 */
export const taperStepDose = (doseValue, unitSource) => {
    const value = Number(doseValue);
    // Zero is finite, and a zero-dose step reaches the chart as a due dose of nothing. A step
    // that gives nothing is a hold, which states a reason.
    if (!Number.isFinite(value) || value <= 0) return undefined;
    const quantity = { value };
    if (unitSource?.unit) quantity.unit = unitSource.unit;
    if (unitSource?.system) quantity.system = unitSource.system;
    if (unitSource?.code) quantity.code = unitSource.code;
    return quantity;
};

/**
 * The step a taper continues with, seeded from the one before it.
 *
 * @param {object} previous - a step window
 * @returns {object} window fields, with no key
 */
export const nextTaperStep = (previous) => {
    const next = {
        ...previous,
        times: [...(previous?.times || [])],
        dayOfWeek: [...(previous?.dayOfWeek || [])],
        doseValue: previous?.doseValue,
        durationValue: null,
        durationUnit: 'd',
        // A hold is a decision about one moment and states its reason fresh, so a step
        // seeded from one continues its schedule rather than its pause.
        type: previous?.type === 'hold' ? 'scheduled' : previous?.type,
        holdReasonCode: null,
        holdNotes: '',
    };
    delete next.key;
    return next;
};

/**
 * How much drug the step gives per day: dose x administrations per interval / interval days.
 *
 * The one number that compares two steps whatever they vary — dose, how often, or the interval —
 * so a step that halves the dose while doubling the frequency reads as the flat step it is.
 *
 * @param {{doseValue: *, perInterval: number, period?: number, periodUnit?: string}} step
 * @returns {number|null}
 */
export const taperStepRate = ({ doseValue, perInterval, period, periodUnit } = {}) => {
    const value = Number(doseValue);
    if (!Number.isFinite(value)) return null;
    if (!(Number(perInterval) > 0)) return null;
    return (value * Number(perInterval)) / intervalDays({ period, periodUnit });
};

/**
 * Which steps raise the daily dose (a titration) and which change the regimen and not the
 * exposure (dose and frequency moved in opposite directions by the same factor).
 *
 * @param {Array<{rate: number|null, doseValue: number}>} entries
 * @param {{previousRate?: number|null, previousDoseValue?: number}} [before] - what step 1 descends from
 * @returns {{ascending: number[], flat: number[]}}
 */
export const classifyTaperSteps = (entries, { previousRate = null, previousDoseValue } = {}) => {
    const ascending = [];
    const flat = [];
    (entries || []).forEach((entry, index) => {
        const previous = index > 0 ? entries[index - 1].rate : previousRate;
        const previousDose = index > 0 ? entries[index - 1].doseValue : previousDoseValue;
        const current = entry.rate;
        if (previous === null || previous === undefined || current === null || current === undefined) return;
        // A taper descends. A step that raises the daily dose is a titration, which the schedule
        // would execute on its date with no clinician in the loop.
        if (current > previous) ascending.push(index);
        if (Math.abs(current - previous) <= 1e-9 && entry.doseValue !== previousDose) flat.push(index);
    });
    return { ascending, flat };
};

/**
 * An acknowledgement covers the ascending step the user was shown, not a standing waiver: a step
 * that stops ascending drops its own, and does not carry it if it ascends again.
 *
 * @param {Set<number>} acknowledged
 * @param {number[]} ascending
 * @returns {Set<number>} the same instance when nothing was dropped
 */
export const pruneAcknowledged = (acknowledged, ascending) => {
    const kept = new Set([...acknowledged].filter((index) => ascending.includes(index)));
    return kept.size !== acknowledged.size ? kept : acknowledged;
};

/**
 * Drop a step's index and shift the later ones down with it.
 *
 * Index-keyed sets are positional, so removing a step moves every later one onto a different
 * position. Clearing only the removed index leaves a flag stranded on a step that no longer
 * exists, and moves an acknowledgement onto a step nobody acknowledged.
 *
 * @param {Set<number>} set
 * @param {number} index
 * @returns {Set<number>}
 */
export const reindexAfterRemoval = (set, index) => {
    const next = new Set();
    for (const position of set) {
        if (position === index) continue;
        next.add(position > index ? position - 1 : position);
    }
    return next;
};

/**
 * Whether a step with no times or days yet answers everything its type requires. A preset
 * supplies times and days when it is applied, so a Scheduled step needs only its recurrence;
 * PRN and Hold steps ask exactly what the patient taper asks.
 *
 * @param {object} window
 * @returns {boolean}
 */
export const isTemplateStepComplete = (window) => {
    if (!window) return false;
    if (window.type === 'prn' || window.type === 'hold') return isWindowComplete(window);
    return Number(window.period) > 0 && Number(window.frequency) >= 1;
};
